/**
 * Characters that should not be encoded in URL parameters
 * These are typically used for placeholders or special formatting
 */
export const ENCODING_WHITELIST_CHARS = ['{', '}'];

/**
 * Helper function to check if a string is already encoded
 * by looking for % followed by two hex digits
 */
export function isEncoded(str: string): boolean {
  return /%[0-9A-Fa-f]{2}/.test(str);
}

/**
 * Encodes a string using encodeURIComponent but preserves whitelisted characters.
 * Prevents double-encoding by checking if the string is already encoded.
 *
 * @param str - The string to encode
 * @param whitelist - Optional array of characters to preserve (not encode)
 * @returns Encoded string with whitelisted characters preserved
 *
 * @example
 * encodeWithWhitelist('hello world')
 * // Returns: 'hello%20world'
 *
 * @example
 * encodeWithWhitelist('hello%20world') // Already encoded
 * // Returns: 'hello%20world' (not double-encoded)
 *
 * @example
 * encodeWithWhitelist('label={test}', ['{', '}'])
 * // Returns: 'label={test}' (curly braces preserved)
 */
export function encodeWithWhitelist(str: string, whitelist?: string[]): string {
  // If already encoded, return as-is to prevent double-encoding
  if (isEncoded(str)) return str;

  let encoded = encodeURIComponent(str);

  // Replace encoded versions of whitelisted characters back to original
  whitelist?.forEach(char => {
    const encodedChar = encodeURIComponent(char);
    const regex = new RegExp(encodedChar, 'g');

    encoded = encoded.replace(regex, char);
  });

  return encoded;
}

/**
 * Encodes query parameter values while preserving parameter keys and separators.
 *
 * This function processes a query string (without the leading `?`) and encodes only the
 * parameter values, leaving the parameter keys and `&` separators unchanged.
 *
 * Behavior:
 * - Skips encoding if the query string is already encoded (contains %XX patterns)
 * - Splits parameters by `&` and processes each individually
 * - For `key=value` pairs: encodes only the value, preserves the key and `=`
 * - For parameters without `=`: encodes the entire parameter
 * - Handles edge case where values contain `=` characters (e.g., `key=value1=value2`)
 *
 * @param queryString - The query string to encode (without leading `?`)
 * @param whitelist - Optional array of characters to preserve (not encode) in values
 * @returns The query string with encoded values
 *
 * @example
 * encodeParams('foo=bar&baz=qux')
 * // Returns: 'foo=bar&baz=qux'
 *
 * @example
 * encodeParams('redirect=https://example.com/page?id=123')
 * // Returns: 'redirect=https%3A%2F%2Fexample.com%2Fpage%3Fid%3D123'
 *
 * @example
 * encodeParams('key1=value&key2=a=b&key3=test')
 * // Returns: 'key1=value&key2=a%3Db&key3=test' (handles values with = in them)
 *
 * @example
 * encodeParams('label={placeholder}', ['{', '}'])
 * // Returns: 'label={placeholder}' (curly braces preserved)
 */
function encodeParams(queryString: string, whitelist?: string[]): string {
  if (!queryString) return '';

  if (isEncoded(queryString)) return queryString;

  // Split by & to get individual parameters
  const params = queryString.split('&');

  return params
    .map(param => {
      const [key, ...valueParts] = param.split('=');
      const value = valueParts.join('='); // Handles values with "=" in them

      if (valueParts.length === 0) {
        // No "=" found, encode the whole param
        return encodeWithWhitelist(param, whitelist);
      }

      // Encode only the value, preserve the "key" and "=" separator
      return `${key}=${encodeWithWhitelist(value, whitelist)}`;
    })
    .join('&');
}

/**
 * Encodes query parameter values in a URL while preserving the URL structure.
 *
 * URL Structure:
 * ```
 * base-url ? param1=value1 & param2=value2 & param3=value3 # hashParam=hashValue
 *          ↑       ↑       ↑       ↑       ↑       ↑       ↑
 *          NOT encoded (structural separators)
 * ```
 *
 * What gets preserved (NOT encoded):
 * - Base URL
 * - Top-level `?` separator
 * - Top-level `&` separators between parameters
 * - Top-level `=` separators between keys and values
 * - Top-level `#` separator and everything after it (hash fragments)
 * - Any characters specified in the optional whitelist parameter
 *
 * What gets encoded:
 * - Parameter values (including nested URLs with their `?`, `&`, `=`, `:`, `/`, etc.)
 *
 * Prevents double-encoding by checking if the string is already encoded (contains %XX patterns).
 *
 * @param url - The URL to encode
 * @param whitelist - Optional array of characters to preserve (not encode) in parameter values
 * @returns The URL with encoded query parameter values with preserved structure
 *
 * @example
 * // URL with nested URL in parameter value
 * safeEncodeUrlParams('https://app.adjust.com/abc?param1=1&param2=https://apps.apple.com/?param3=123')
 * // Returns: 'https://app.adjust.com/abc?param1=1&param2=https%3A%2F%2Fapps.apple.com%2F%3Fparam3%3D123'
 *
 * @example
 * // URL with query parameters and hash fragment (hash is preserved unchanged)
 * safeEncodeUrlParams('https://app.adjust.com/abc?param1=1&param2=https://apps.apple.com/?param3=123#hashParam1=a&hashParam2=b')
 * // Returns: 'https://app.adjust.com/abc?param1=1&param2=https%3A%2F%2Fapps.apple.com%2F%3Fparam3%3D123#hashParam1=a&hashParam2=b'
 *
 * @example
 * // Simple URL with alphanumeric values (no encoding needed)
 * safeEncodeUrlParams('/editor?product_id=5926&showProductSelection=1')
 * // Returns: '/editor?product_id=5926&showProductSelection=1'
 *
 * @example
 * // URL with only hash fragment (no query parameters)
 * safeEncodeUrlParams('https://www.pixum.de/editor#product_id=5885&design_id=100039')
 * // Returns: 'https://www.pixum.de/editor#product_id=5885&design_id=100039'
 *
 * @example
 * // URL with curly braces preserved using whitelist
 * safeEncodeUrlParams('https://example.com?label={placeholder}', ['{', '}'])
 * // Returns: 'https://example.com?label={placeholder}'
 */
export function safeEncodeUrlParams(url: string, whitelist?: string[]): string {
  if (!url) return '';

  const [baseUrl, ...queryParts] = url.split('?');

  if (queryParts.length === 0) {
    return url;
  }

  const queryString = queryParts.join('?');

  // Check if there's a hash fragment
  const [queryParams, ...hashParts] = queryString.split('#');

  const encodedQueryString = encodeParams(queryParams, whitelist);

  if (hashParts.length > 0) {
    const hashFragments = hashParts.join('#');

    return `${baseUrl}?${encodedQueryString}#${hashFragments}`;
  }

  return `${baseUrl}?${encodedQueryString}`;
}
